import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Card,
  Table,
  Tag,
  Typography,
  Space,
  Button,
  Modal,
  Input,
  Row,
  Col,
  Empty,
  Spin,
  Avatar,
  Upload,
  Tooltip,
  Dropdown,
} from "antd";
import {
  MessageOutlined,
  PhoneOutlined,
  VideoCameraOutlined,
  MoreOutlined,
  PaperClipOutlined,
  SmileOutlined,
  LinkOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import AttachIcon from "../assets/images/paperclip (1).png";
import SendIcon from "../assets/images/send.png";
import useRequest from "../hooks/useRequest";
import { ShowToast, Severty } from "../helper/toast";
import { S3Client } from "@aws-sdk/client-s3";
import { Upload as S3Upload } from "@aws-sdk/lib-storage";
// Emoji picker library – make sure to install:
// npm install emoji-picker-react
import EmojiPicker from "emoji-picker-react";
import EmojiIcon from "../assets/images/happy.png";
import { mainSocket, sendMainSocketEvent } from "../adminSessionSocket";
import apiPath from "../constants/apiPath";
import { EllipsisOutlined } from "@ant-design/icons";
const { confirm } = Modal;
const { Title, Text } = Typography;
const { TextArea } = Input;

const IMAGE_EXT_RE = /\.(png|jpe?g|gif|webp|bmp|svg)(\?.*)?$/i;
const s3Config = {
  region: "us-east-1",
  credentials: {
    accessKeyId: "IJTXH751MBXUWMTWM0SF",
    secretAccessKey: "lfTPRJ2PqAxsp3poTW9YlHktgaL1cFkLup8LidW9",
  },
  endpoint: "https://api-ap-south-mum-1.openstack.acecloudhosting.com:8080/",
  forcePathStyle: true,
};
const s3Client = new S3Client(s3Config);

const getTimeValue = (value) => {
  if (!value) return 0;
  const ts = new Date(value).getTime();
  return Number.isNaN(ts) ? 0 : ts;
};

const normalizeTicketKey = (value) => {
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

const ticketMatches = (active, incoming) => {
  const a = normalizeTicketKey(active);
  const b = normalizeTicketKey(incoming);
  if (!a || !b) return false;

  const aNum = Number(a);
  const bNum = Number(b);
  if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
    return aNum === bNum;
  }
  return a.toLowerCase() === b.toLowerCase();
};

function resolveMediaUrl(raw) {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s) return null;
  if (s.startsWith("blob:") || s.startsWith("data:image")) return s;
  if (/^https?:\/\//i.test(s)) return s;
  const base = String(apiPath.baseURL || "").replace(/\/$/, "");
  if (!base) return s;
  const path = s.startsWith("/") ? s : `/${s}`;
  return `${base}${path}`;
}

function extractImageUrlFromRecord(msg) {
  if (!msg || typeof msg !== "object") return null;

  const valueToUrl = (v) => {
    if (v == null) return null;
    if (typeof v === "string") {
      const s = v.trim();
      return s ? resolveMediaUrl(s) : null;
    }
    if (typeof v === "object") {
      const nested = [
        v.url,
        v.image_url,
        v.file_url,
        v.path,
        v.location,
        v.Location,
        v.src,
      ];
      for (const candidate of nested) {
        const u = valueToUrl(candidate);
        if (u) return u;
      }
    }
    return null;
  };

  const pick = (...keys) => {
    for (const k of keys) {
      const v = msg[k];
      const url = valueToUrl(v);
      if (url) return url;
    }
    return null;
  };

  const nestedPick = (obj, ...keys) => {
    if (!obj || typeof obj !== "object") return null;
    for (const k of keys) {
      const v = obj[k];
      const url = valueToUrl(v);
      if (url) return url;
    }
    return null;
  };

  const fromFields = pick(
    "image_url",
    "imageUrl",
    "image",
    "file",
    "file_url",
    "fileUrl",
    "attachment_url",
    "attachment",
    "media_url",
    "file_path",
  );
  if (fromFields) return fromFields;

  const fromMeta = nestedPick(msg.meta, "image_url", "image", "file", "file_url");
  if (fromMeta) return fromMeta;

  const fromData = nestedPick(msg.data, "image_url", "image", "file", "file_url");
  if (fromData) return fromData;

  const firstAttachment = Array.isArray(msg.attachments)
    ? msg.attachments[0]
    : null;
  const fromAttachments =
    nestedPick(firstAttachment, "url", "image_url", "file_url", "path") ||
    (typeof firstAttachment === "string" ? firstAttachment : null);
  if (fromAttachments) return resolveMediaUrl(fromAttachments);

  const text = String(msg.message ?? "").trim();
  if (text && IMAGE_EXT_RE.test(text.split("?")[0])) {
    return resolveMediaUrl(text);
  }
  return null;
}

function getFileNameFromUrl(url) {
  if (!url) return "";
  try {
    const withoutQuery = String(url).split("?")[0];
    const last = withoutQuery.split("/").pop() || "";
    return decodeURIComponent(last);
  } catch (_) {
    return "";
  }
}

function extractAttachmentMetaFromRecord(msg) {
  const url = extractImageUrlFromRecord(msg);
  if (!url) return null;

  const firstAttachment = Array.isArray(msg?.attachments)
    ? msg.attachments[0]
    : null;
  const declaredType =
    msg?.file_type ||
    msg?.mime_type ||
    msg?.meta?.file_type ||
    msg?.data?.file_type ||
    firstAttachment?.type ||
    firstAttachment?.mime_type ||
    "";
  const declaredName =
    msg?.file_name ||
    msg?.filename ||
    msg?.meta?.file_name ||
    msg?.data?.file_name ||
    firstAttachment?.name ||
    "";
  const messageText = String(msg?.message || "").trim().toLowerCase();
  const hasImageField =
    Boolean(msg?.image_url || msg?.imageUrl || msg?.image) ||
    Boolean(msg?.meta?.image_url || msg?.meta?.image) ||
    Boolean(msg?.data?.image_url || msg?.data?.image) ||
    Boolean(firstAttachment?.image_url);
  const isImage =
    String(declaredType).toLowerCase().startsWith("image/") ||
    IMAGE_EXT_RE.test(String(url).split("?")[0]) ||
    hasImageField ||
    messageText === "image";

  return {
    url,
    isImage,
    name: declaredName || getFileNameFromUrl(url) || "Attachment",
  };
}

function displayTextForMessage(rawText, imageUrl) {
  const t = String(rawText ?? "").trim();
  if (!t) return "";
  if (imageUrl && t.toLowerCase() === "image") return "";
  if (imageUrl && /^https?:\/\//i.test(t)) return "";
  if (!imageUrl) return t;
  const normImg = imageUrl.split("?")[0];
  const normT = t.split("?")[0];
  if (normT === normImg) return "";
  const fileFromUrl = normImg.split("/").pop() || "";
  if (fileFromUrl && t === fileFromUrl) return "";
  return t;
}

function formatChatMessageFromServer(msg) {
  const attachment = extractAttachmentMetaFromRecord(msg);
  const imageUrl = attachment?.isImage ? attachment.url : null;
  const text = displayTextForMessage(msg.message, imageUrl);
  const created = msg.created_at ? new Date(msg.created_at) : new Date();

  return {
    id: msg.id,
    sender: !msg.sender && msg.sender_type === "ADMIN" ? "admin" : "user",
    text,
    imageUrl: imageUrl || undefined,
    attachmentUrl: attachment?.isImage ? undefined : attachment?.url || undefined,
    attachmentName: attachment?.isImage ? undefined : attachment?.name || undefined,
    time: created.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    }),
  };
}

function revokeBlobUrlsInMessages(messages) {
  (messages || []).forEach((m) => {
    const candidates = [m?.imageUrl, m?.attachmentUrl];
    candidates.forEach((u) => {
      if (!(u && String(u).startsWith("blob:"))) return;
      try {
        URL.revokeObjectURL(u);
      } catch (_) {
        /* ignore */
      }
    });
  });
}

async function uploadImageToS3(file) {
  const safeName = file?.name ? file.name.replace(/\s+/g, "_") : `img_${Date.now()}.png`;
  const key = `support/chat/${Date.now()}_${safeName}`;
  const upload = new S3Upload({
    client: s3Client,
    params: {
      Bucket: "invent-colab-obj-bucket",
      Key: key,
      Body: file,
      ContentType: file?.type || "application/octet-stream",
    },
  });
  const result = await upload.done();
  return result?.Location || null;
}

const buildSupportMessagePayloads = ({ text, fileUrl, file }) => {
  const normalizedText = text || "";
  if (!fileUrl) {
    return [{ message: normalizedText }];
  }

  const fileName = file?.name || "";
  const fileType = file?.type || "";

  return [
    // Keep current payload shape first.
    {
      message: normalizedText,
      file_url: fileUrl,
    },
    // Fallbacks for APIs that expect different attachment keys.
    {
      message: normalizedText,
      attachment_url: fileUrl,
      file_name: fileName,
      file_type: fileType,
    },
    {
      message: normalizedText,
      file: fileUrl,
      file_name: fileName,
      file_type: fileType,
    },
  ];
};

const MOCK_TICKETS = [
  {
    id: "SUP-1001",
    userName: "John Doe",
    subject: "Unable to add bank account",
    status: "open",
    createdAt: "2026-03-10 14:12",
  },
  {
    id: "SUP-1002",
    userName: "Jane Smith",
    subject: "Cashback not received",
    status: "closed",
    createdAt: "2026-03-09 09:45",
  },
  {
    id: "SUP-1003",
    userName: "Michael Johnson",
    subject: "KYC verification delayed",
    status: "open",
    createdAt: "2026-03-11 18:20",
  },
];

const MOCK_MESSAGES = {
  "SUP-1001": [
    {
      id: 1,
      sender: "user",
      text: "Hi, I'm unable to add my bank account.",
      time: "14:10",
    },
    {
      id: 2,
      sender: "admin",
      text: "Hello John, can you please share the error message you see?",
      time: "14:11",
    },
  ],
  "SUP-1003": [
    {
      id: 1,
      sender: "user",
      text: "My KYC has been pending for 3 days.",
      time: "18:18",
    },
    {
      id: 2,
      sender: "admin",
      text: "We are checking this with the compliance team.",
      time: "18:19",
    },
  ],
};

const Support = () => {
  const { request } = useRequest();
  const location = useLocation();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chatVisible, setChatVisible] = useState(false);
  const [activeTicket, setActiveTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const messagesEndRef = useRef(null);
  const isInitialRender = useRef(true);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [chatLoading, setChatLoading] = useState(false);
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const [attachmentPreviewUrl, setAttachmentPreviewUrl] = useState(null);
  const activeTicketRef = useRef(null);
  const chatVisibleRef = useRef(false);
  const realtimeSyncTimerRef = useRef(null);
  const joinedTicketRef = useRef(null);
  const joinedSocketRef = useRef(null);

  useEffect(() => {
    if (!attachment) {
      setAttachmentPreviewUrl(null);
      return;
    }
    if (attachment.type?.startsWith("image/")) {
      const url = URL.createObjectURL(attachment);
      setAttachmentPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setAttachmentPreviewUrl(null);
  }, [attachment]);

  const fetchTickets = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    params.append("page", pagination.current);
    params.append("limit", pagination.pageSize);

    request({
      url: `/api/admin/support-ticket`,
      method: "GET",
      onSuccess: (data) => {
        setLoading(false);
        let sessionData = [];
        let totalCount = 0;
        let currentPageFromApi = undefined;
        let limitFromApi = undefined;
        if (data?.data) {
          // Common shapes: { data: { sessions, pagination } } or { data: { docs, totalDocs } }
          if (Array.isArray(data.data)) {
            sessionData = data.data;
          }
          const meta = data.data.pagination || data.pagination || {};
          totalCount =
            meta.totalCount ?? meta.total ?? data.data.totalDocs ?? 0;
          currentPageFromApi = meta.currentPage ?? meta.page ?? meta.pageNumber;
          limitFromApi = meta.limit ?? meta.pageSize;
        } else if (Array.isArray(data)) {
          sessionData = data;
          totalCount = data.length;
        }

        const sortedTickets = Array.isArray(sessionData)
          ? [...sessionData].sort((a, b) => {
            const aTs = Math.max(
              getTimeValue(a?.last_message_at),
              getTimeValue(a?.updated_at),
              getTimeValue(a?.created_at),
            );
            const bTs = Math.max(
              getTimeValue(b?.last_message_at),
              getTimeValue(b?.updated_at),
              getTimeValue(b?.created_at),
            );
            return bTs - aTs;
          })
          : [];

        setTickets(sortedTickets);
        setPagination((prev) => ({
          ...prev,
          total: totalCount,
          current: currentPageFromApi ?? prev.current,
          pageSize: limitFromApi ?? prev.pageSize,
        }));
      },
      onError: (error) => {
        setLoading(false);
        setTickets([]);
        setPagination((prev) => ({ ...prev, total: 0 }));
        console.log("Error occured while getting active session", error);
        // ShowToast("Failed to fetch sessions", Severty.ERROR);
      },
    });
  }, [pagination.current, pagination.pageSize, request]);

  const getSenderType = (msg) => {
    return !msg.sender && msg.sender_type === "ADMIN" ? "admin" : "user";
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    fetchTickets();
  }, [pagination.current, pagination.pageSize]);

  useEffect(() => {
    if (!chatVisible) return;
    // Small timeout ensures the DOM has fully painted new messages
    // and modal animations have progressed enough before scrolling.
    setTimeout(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }, 150);
  }, [messages, chatVisible]);

  useEffect(() => {
    activeTicketRef.current = activeTicket;
  }, [activeTicket]);

  useEffect(() => {
    chatVisibleRef.current = chatVisible;
  }, [chatVisible]);

  const scheduleRealtimeSync = (ticketId) => {
    if (realtimeSyncTimerRef.current) {
      clearTimeout(realtimeSyncTimerRef.current);
    }
    realtimeSyncTimerRef.current = setTimeout(() => {
      if (!chatVisibleRef.current || !ticketId) return;
      fetchMessages(ticketId, { silent: true });
    }, 120);
  };

  useEffect(() => {
    return () => {
      if (realtimeSyncTimerRef.current) {
        clearTimeout(realtimeSyncTimerRef.current);
      }
    };
  }, []);

  const emitJoinTicket = useCallback((ticketId) => {
    if (!ticketId) return;
    if (
      joinedTicketRef.current &&
      ticketMatches(joinedTicketRef.current, ticketId) &&
      joinedSocketRef.current === mainSocket
    ) {
      return;
    }
    const sent = sendMainSocketEvent(
      {
        type: "join_ticket",
        ticket: ticketId,
        ticket_id: ticketId,
      },
      "join_ticket",
    );
    if (sent) {
      joinedTicketRef.current = ticketId;
      joinedSocketRef.current = mainSocket;
    }
  }, []);

  const emitLeaveTicket = useCallback((ticketId) => {
    if (!ticketId) return;
    const sent = sendMainSocketEvent(
      {
        type: "leave_ticket",
        ticket: ticketId,
        ticket_id: ticketId,
      },
      "leave_ticket",
    );
    if (sent && ticketMatches(joinedTicketRef.current, ticketId)) {
      joinedTicketRef.current = null;
      joinedSocketRef.current = null;
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ticketIdFromQuery = params.get("ticket");
    if (!ticketIdFromQuery) return;

    const existingTicket =
      tickets.find((t) =>
        ticketMatches(
          t?.ticket_id ?? t?.ticket ?? t?.id,
          ticketIdFromQuery,
        ),
      ) || null;

    const ticketToOpen =
      existingTicket || {
        id: ticketIdFromQuery,
        user: { full_name: "User" },
        title: `Ticket #${ticketIdFromQuery}`,
        status: "OPEN",
      };

    openChat(ticketToOpen);
    navigate("/support", { replace: true });
  }, [location.search, tickets]);

  const openChat = (ticket) => {
    setActiveTicket(ticket);
    setMessages((prev) => {
      revokeBlobUrlsInMessages(prev);
      return [];
    });
    setNewMessage("");
    setAttachment(null);
    setEmojiPickerOpen(false);
    setChatVisible(true);

    fetchMessages(ticket.id);
  };

  useEffect(() => {
    if (!mainSocket || !activeTicket?.id || !chatVisible) return;

    const tryJoin = () => {
      try {
        emitJoinTicket(activeTicket.id);
      } catch (e) {
        console.error("join_ticket send failed", e);
      }
    };

    if (mainSocket.readyState === WebSocket.OPEN) {
      tryJoin();
      return;
    }

    mainSocket.addEventListener("open", tryJoin);
    return () => {
      mainSocket.removeEventListener("open", tryJoin);
    };
  }, [activeTicket, chatVisible]);

  useEffect(() => {
    const handleBridgeMessage = (event) => {
      try {
        if (!chatVisibleRef.current) return;

        const res = event?.detail;
        if (!res) return;

        const isWrapped = res && typeof res === "object" && "type" in res;
        const eventType = isWrapped ? res.type : "ticket_message";
        const data = isWrapped ? res.data : res;
        if (
          !data ||
          (eventType !== "ticket_message" && eventType !== "notification")
        ) {
          return;
        }

        const currentTicketId =
          activeTicketRef.current?.ticket_id ??
          activeTicketRef.current?.ticket ??
          activeTicketRef.current?.id;
        const incomingTicketId = data.ticket ?? data.ticket_id ?? data.id;
        if (!ticketMatches(currentTicketId, incomingTicketId)) {
          return;
        }

        const isCurrentJoinedTicket =
          joinedTicketRef.current &&
          ticketMatches(joinedTicketRef.current, incomingTicketId);
        if (isCurrentJoinedTicket) {
          console.log("[SupportSocket] received message for joined ticket", {
            joinedTicketId: joinedTicketRef.current,
            incomingTicketId,
            eventType,
            payload: data,
          });
        }

        if (String(data?.type || "").toLowerCase() === "status_update") {
          return;
        }

        const hasRenderableMessage =
          data?.message ||
          data?.file ||
          data?.file_url ||
          data?.image ||
          data?.image_url ||
          data?.attachments;
        if (!hasRenderableMessage) {
          return;
        }

        setMessages((prev) => {
          if (data?.id && prev.find((msg) => msg.id === data.id)) return prev;
          return [...prev, formatChatMessageFromServer(data)];
        });
        scheduleRealtimeSync(currentTicketId);
      } catch (e) {
        console.error("app-socket-message parse error", e);
      }
    };

    window.addEventListener("app-socket-message", handleBridgeMessage);
    return () => {
      window.removeEventListener("app-socket-message", handleBridgeMessage);
    };
  }, []);

  const fetchMessages = (ticketId, options = {}) => {
    const { silent = false } = options;
    if (!silent) {
      setChatLoading(true);
    }

    request({
      url: `/support/tickets/${ticketId}/messages`,
      method: "GET",
      onSuccess: (res) => {
        const msgs = Array.isArray(res?.data) ? [...res.data] : [];
        msgs.sort(
          (a, b) => getTimeValue(a?.created_at) - getTimeValue(b?.created_at),
        );
        const formatted = msgs.map(formatChatMessageFromServer);

        setMessages((prev) => {
          revokeBlobUrlsInMessages(prev);
          return formatted;
        });
        if (!silent) {
          setChatLoading(false);
        }
      },
      onError: (err) => {
        console.error("Failed to fetch messages", err);
        if (!silent) {
          setMessages([]);
          setChatLoading(false);
        }
      },
    });
  };

  const handleSend = async () => {
    const trimmed = newMessage.trim();
    if (!trimmed && !attachment) return;
    const uploadFile =
      attachment instanceof Blob
        ? attachment
        : attachment?.originFileObj instanceof Blob
          ? attachment.originFileObj
          : null;

    if (attachment && !uploadFile) {
      ShowToast("Selected file is invalid. Please reselect image.", Severty.ERROR);
      return;
    }

    const now = new Date();
    const optimisticId = `local-${now.getTime()}`;
    const imageBlobUrl =
      uploadFile?.type?.startsWith("image/") ? URL.createObjectURL(uploadFile) : null;

    const localMessage = {
      id: optimisticId,
      sender: "admin",
      text: trimmed || "",
      imageUrl: imageBlobUrl || undefined,
      attachmentUrl:
        uploadFile && !imageBlobUrl ? undefined : imageBlobUrl || undefined,
      attachmentName: uploadFile && !imageBlobUrl ? uploadFile.name : undefined,
      time: now.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
    };

    // Show message in UI immediately without reloading whole chat
    setMessages((prev) => [...prev, localMessage]);
    setNewMessage("");

    try {
      let uploadedImageUrl = null;
      if (uploadFile) {
        uploadedImageUrl = await uploadImageToS3(uploadFile);
        if (!uploadedImageUrl) {
          throw new Error("Image upload failed");
        }
      }

      const payloads = buildSupportMessagePayloads({
        text: trimmed,
        fileUrl: uploadedImageUrl,
        file: uploadFile,
      });

      let sendError = null;
      let sent = false;
      for (const payload of payloads) {
        console.log("send-message payload =>", payload);
        const res = await request({
          url: `/support/tickets/${activeTicket.id}/send-message`,
          method: "POST",
          data: payload,
          onError: (err) => {
            sendError = err;
          },
        });
        if (res) {
          sent = true;
          break;
        }
      }

      if (!sent) {
        throw sendError || new Error("Failed to send message");
      }

      if (uploadedImageUrl) {
        const isImage = uploadFile?.type?.startsWith("image/");
        setMessages((prev) =>
          prev.map((m) =>
            m.id === optimisticId
              ? {
                ...m,
                imageUrl: isImage ? uploadedImageUrl : undefined,
                attachmentUrl: isImage ? undefined : uploadedImageUrl,
              }
              : m,
          ),
        );
      }
      setAttachment(null);
      setEmojiPickerOpen(false);
    } catch (error) {
      if (imageBlobUrl) {
        try {
          URL.revokeObjectURL(imageBlobUrl);
        } catch (_) {
          /* ignore */
        }
      }
      setMessages((prev) => prev.filter((m) => m.id !== optimisticId));
      ShowToast(
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to send message",
        Severty.ERROR,
      );
    }
  };

  const getInitials = (name = "") => {
    const parts = name.trim().split(" ");
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
  };

  const columns = useMemo(
    () => [
      {
        title: "Ticket ID",
        dataIndex: "id",
        key: "id",
        render: (id) => <Text code>Ticket_{id}</Text>,
      },
      {
        title: "User",
        key: "user",
        render: (_, record) => ((record?.user?.first_name ?? "") + " " + (record?.user?.middle_name ?? "") + " " + (record?.user?.last_name ?? "") || "-"),
      },
      {
        title: "Subject",
        key: "subject",
        render: (_, record) => record?.title || "-",
        ellipsis: true,
      },
      {
        title: "Related Transaction",
        key: "txn_id",
        render: (_, record) =>
          record?.txn_id ? (
            <Tooltip title={record?.txn_id}>
              <Button
                type="default"
                icon={<LinkOutlined />}
                size="small"
                style={{ maxWidth: 190 }}
                onClick={() => {
                  navigate(`/transaction-manager?txn_id=${record.txn_id}`);
                }}
              >
                <span
                  style={{
                    display: "inline-block",
                    maxWidth: 130,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    verticalAlign: "bottom",
                  }}
                >
                  {record.txn_id}
                </span>
              </Button>
            </Tooltip>
          ) : (
            "NA"
          ),
        ellipsis: true,
        width: 220,
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        render: (status) => (
          <Tag color={status === "OPEN" ? "green" : "red"}>
            {status === "OPEN" ? "Open" : "Closed"}
          </Tag>
        ),
      },
      {
        title: "Created",
        key: "createdAt",
        render: (_, record) => new Date(record.created_at).toLocaleString(),
      },
      {
        title: "Actions",
        key: "actions",
        render: (_, record) =>
          record.status === "OPEN" ? (
            <Button
              type="primary"
              icon={<MessageOutlined />}
              size="small"
              onClick={() => openChat(record)}
            >
              Chat
            </Button>
          ) : (
            // <Text type="secondary" style={{ fontSize: 12 }}>
            //   No actions
            // </Text>
            ""
          ),
      },
    ],
    [],
  );

  const showConfirm = (ticketId) => {
    confirm({
      title: "Are you sure?",
      content: "Do you really want to Close the Ticket?",
      okText: "Yes",
      cancelText: "No",
      onOk() {
        request({
          url: `/api/admin/support-ticket/${ticketId}`,
          method: "PUT",
          onSuccess: () => {
            fetchTickets();

            // ✅ CLOSE MODAL HERE
            setChatVisible(false);

            // optional: cleanup
            setActiveTicket(null);
            setMessages([]);
          },
          onError: (error) => {
            ShowToast(
              error?.response?.data?.message || "Failed to close ticket",
              Severty.ERROR
            );
          },
        });
      },
    });
  };

  const items = [
    {
      key: "close-ticket",
      label: "Close Ticket",
    },
  ];


  return (
    <div className="support-page">
      <Card
        className="hover-card"
        style={{
          borderRadius: 20,
        }}
      >
        <Row justify="space-between" align="middle" gutter={[16, 16]}>
          <Col xs={24} md="auto">
            <Title level={3} style={{ margin: 0 }}>
              Support Tickets
            </Title>
            <Text type="secondary">
              Manage user queries and respond in real-time.
            </Text>
          </Col>
        </Row>
      </Card>

      <Card
        style={{
          borderRadius: 16,
        }}
        bodyStyle={{ padding: 16 }}
      >
        {loading ? (
          <div style={{ textAlign: "center", padding: "80px 0" }}>
            <Spin size="large" />
            <div style={{ marginTop: 12 }}>
              <Text type="secondary">Loading support tickets...</Text>
            </div>
          </div>
        ) : tickets.length === 0 ? (
          <div style={{ padding: "60px 0" }}>
            <Empty
              description={
                <span style={{ color: "#64748b" }}>No tickets found</span>
              }
            />
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={tickets}
            rowKey="id"
            pagination={{ pageSize: 10 }}
          />
        )}
      </Card>

      <Modal
        open={chatVisible}
        centered
        className="support-chat-modal tab-modal"
        title={
          <div className="support-chat-header">
            <div className="support-chat-header-left">
              <div className="support-chat-header-left-content">
                <Avatar
                  size={40}
                  style={{ backgroundColor: "#e5f3ff", color: "#0f172a" }}
                >
                  {getInitials(activeTicket?.user?.full_name || "User")}
                </Avatar>
                <div className="support-chat-title">
                  <span className="support-chat-name">
                    {`${activeTicket?.user?.first_name ?? ""} ${activeTicket?.user?.middle_name ?? ""} ${activeTicket?.user?.last_name ?? ""}: ${activeTicket?.title}` || "User"}
                  </span>
                  {/* <span className="support-chat-status">Online</span> */}
                </div>
              </div>
              <Button
                onClick={() => showConfirm(activeTicket?.id)}
                icon={<CloseOutlined />}
                className="support-chat-close-button"
              >
                Close Ticket
              </Button>
            </div>
          </div>
        }
        onCancel={() => {
          emitLeaveTicket(activeTicket?.id);
          joinedTicketRef.current = null;
          joinedSocketRef.current = null;
          setChatVisible(false);
          setAttachment(null);
          setEmojiPickerOpen(false);
          setActiveTicket(null);
        }}
        footer={null}
        width={720}
        bodyStyle={{ padding: 0 }}
      >
        <div className="support-chat-container">
          <div className="support-chat-messages">
            {chatLoading ? (
              <Spin />
            ) : (
              messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`support-chat-row ${msg.sender === "admin"
                    ? "support-chat-row-admin"
                    : "support-chat-row-user"
                    }`}
                >
                  <div
                    className={`support-chat-bubble ${msg.sender === "admin"
                      ? "support-chat-bubble-admin"
                      : "support-chat-bubble-user"
                      }`}
                  >
                    <div className="support-chat-text">
                      {msg.imageUrl ? (
                        <img
                          src={msg.imageUrl}
                          alt=""
                          className="support-chat-bubble-image"
                          loading="lazy"
                        />
                      ) : null}
                      {!msg.imageUrl && msg.attachmentName ? (
                        msg.attachmentUrl ? (
                          <a
                            href={msg.attachmentUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="support-chat-text-caption"
                          >
                            <PaperClipOutlined style={{ marginRight: 6 }} />
                            {msg.attachmentName}
                          </a>
                        ) : (
                          <div className="support-chat-text-caption">
                            <PaperClipOutlined style={{ marginRight: 6 }} />
                            {msg.attachmentName}
                          </div>
                        )
                      ) : null}
                      {msg.text ? (
                        <div
                          className={
                            msg.imageUrl || msg.attachmentName
                              ? "support-chat-text-caption"
                              : undefined
                          }
                        >
                          {msg.text}
                        </div>
                      ) : null}
                      {!msg.imageUrl && !msg.attachmentName && !msg.text ? (
                        <span className="support-chat-text-empty">—</span>
                      ) : null}
                    </div>
                    <div className="support-chat-time">{msg.time}</div>
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} style={{ paddingBottom: '20px' }} />
          </div>

          <div className="support-chat-input-wrap">
            {attachment && (
              <div className="support-attachment-preview-bar">
                {attachmentPreviewUrl ? (
                  <div className="support-attachment-preview-image-wrap">
                    <img
                      src={attachmentPreviewUrl}
                      alt={attachment.name}
                      className="support-attachment-preview-image"
                    />
                    <button
                      type="button"
                      className="support-attachment-preview-remove"
                      aria-label="Remove attachment"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setAttachment(null);
                      }}
                    >
                      ×
                    </button>
                  </div>
                ) : (
                  <div className="support-attachment-preview-file">
                    <PaperClipOutlined />
                    <span className="support-attachment-preview-filename">
                      {attachment.name}
                    </span>
                    <button
                      type="button"
                      className="support-attachment-preview-remove is-inline"
                      aria-label="Remove attachment"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setAttachment(null);
                      }}
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>
            )}
            <div className="support-chat-input-row">
              <div className="support-chat-input-inner">
                <Upload
                  name="file"
                  showUploadList={false}
                  beforeUpload={() => false}
                  openFileDialogOnClick
                  maxCount={1}
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                  onChange={(info) => {
                    const raw =
                      info?.file?.originFileObj ||
                      (info?.file instanceof File ? info.file : null);
                    if (raw) setAttachment(raw);
                  }}
                >
                  <button
                    type="button"
                    className="support-attach-trigger"
                    aria-label="Attach file"
                  >
                    <img src={AttachIcon} className="support-attach-btn" alt="" />
                  </button>
                </Upload>
                <div
                  className="support-emoji-trigger"
                  onClick={() => setEmojiPickerOpen((prev) => !prev)}
                >
                  <img src={EmojiIcon} className="emojiss-attach-btn" />
                  {emojiPickerOpen && (
                    <div className="support-emoji-picker">
                      <EmojiPicker
                        onEmojiClick={(emojiData) => {
                          const symbol = emojiData.emoji;
                          setNewMessage((prev) => (prev || "") + symbol);
                        }}
                        searchDisabled
                        skinTonesDisabled
                        previewConfig={{ showPreview: false }}
                        width={280}
                        height={320}
                      />
                    </div>
                  )}
                </div>
                <Input
                  placeholder="Type a message..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onPressEnter={(e) => {
                    if (!e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                />
              </div>
              <Button
                type="primary"
                shape="circle"
                onClick={handleSend}
                className="support-send-btn"
              >
                <img src={SendIcon} className="chat-send-btn" alt="" />
              </Button>

            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Support;
